import { Box, Button, Circle, Heading, HStack, Text } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { toDatetimeLocalValue, toServerDateTime } from "../../../../utils/cairoTime";
import MainModel from "../../../../components/Models/main-model";
import { getAllChaptersRequest } from "../../../../modules/admin-modules/chapters/Actions";
import {
  createExamRequest,
  editExamRequest,
} from "../../../../modules/admin-modules/exams/Actions";
import { getAllGroupsRequest } from "../../../../modules/admin-modules/groups/Actions";
import {
  closeCreateExamDataModel,
  openQuestionsListModel,
} from "../../../../modules/admin-modules/models/Actions";
import { getAllVideosRequest } from "../../../../modules/admin-modules/videos/Actions";
import StepOne from "../forms/step-one";
import StepTwo from "../forms/step-two";
import {
  getChapterId,
  getChapterQuestionCount,
  getChapterType,
  getDisplayChapter,
  getWalletChapters,
} from "../chapter-mapping";

const toStr = (value, fallback = "0") =>
  value == null || value === "" ? fallback : String(value);

const mapExamToFormValues = (exam, isAdd, fixedType) => {
  const lockedType = fixedType != null ? String(fixedType) : null;
  if (isAdd || !exam?.id) {
    return {
      name: "",
      time: "0",
      timer: "0",
      published: "0",
      enable: "0",
      auto_publish_result: "0",
      redo_flag: "0",
      type: lockedType || "1",
      difficulty: "0",
      book_mark: "0",
      random: "",
      sub_type: 2,
      essay_type: "",
      question_number: "",
      success_score: "",
      start_date: "",
      end_date: "",
      video_id: "",
      payment_subject_id: "",
      groups: [],
      chapters: [],
    };
  }

  const displayChapter = getDisplayChapter(exam.chapters);
  const wallets = getWalletChapters(exam.chapters);

  const chapters = [];
  chapters[1] = {
    chapter_id: getChapterId(displayChapter) || "",
    no_of_questions: 0,
    type: "0",
  };
  if (wallets.length) {
    wallets.forEach((chapter, index) => {
      chapters[index + 2] = {
        chapter_id: getChapterId(chapter) || "",
        no_of_questions: getChapterQuestionCount(chapter),
        type: toStr(getChapterType(chapter), "0"),
      };
    });
  } else {
    chapters[2] = {
      chapter_id: "",
      no_of_questions: 0,
      type: "0",
    };
  }

  return {
    name: exam.name || "",
    published: toStr(exam.published),
    enable: toStr(exam.enable),
    auto_publish_result: toStr(exam.auto_publish_result),
    redo_flag: toStr(exam.redo_flag),
    type: lockedType || toStr(exam.type, "1"),
    difficulty: toStr(exam.difficulty),
    book_mark: toStr(exam.book_mark),
    random: exam.random == null ? "" : String(exam.random),
    timer: toStr(exam.timer),
    time: exam.time ?? 0,
    question_number: exam.question_number ?? "",
    success_score: exam.success_score ?? "",
    start_date: toDatetimeLocalValue(exam.start_date),
    end_date: toDatetimeLocalValue(exam.end_date),
    essay_type:
      exam.essay_type != null && exam.essay_type !== ""
        ? String(exam.essay_type)
        : "",
    sub_type: 2,
    video_id: exam.video_id ?? exam.video?.id ?? "",
    payment_subject_id:
      exam.payment_subject_id ??
      exam.payment_subject?.id ??
      exam.payment_subject?.[0]?.id ??
      "",
    groups: exam.groups?.map((group) => group?.id ?? group) ?? [],
    chapters,
  };
};

const normalizeChapters = (chapters) => {
  if (!chapters) return [];
  if (Array.isArray(chapters)) return chapters;
  return Object.keys(chapters)
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => chapters[key]);
};

const appendChapters = (formData, chapters, questionNumber) => {
  const submitted = [];
  let formIndex = 1;

  normalizeChapters(chapters).forEach((chapter) => {
    const chapterId = getChapterId(chapter);
    if (chapterId == null || chapterId === "" || String(chapterId) === "undefined") {
      return;
    }

    const noOfQuestions =
      chapter?.no_of_questions != null && chapter?.no_of_questions !== ""
        ? chapter.no_of_questions
        : submitted.length === 0
          ? 0
          : questionNumber;
    const type =
      chapter?.type != null && chapter?.type !== "" ? chapter.type : 0;

    formData.append(`chapters[${formIndex}][chapter_id]`, chapterId);
    formData.append(`chapters[${formIndex}][no_of_questions]`, noOfQuestions);
    formData.append(`chapters[${formIndex}][type]`, type);
    submitted.push({
      ...chapter,
      chapter_id: chapterId,
      no_of_questions: noOfQuestions,
      type,
    });
    formIndex += 1;
  });

  return submitted;
};

const AddExamData = ({
  isAdd,
  data,
  setSelectedData,
  setExamData,
  fixedType,
  onSaved,
}) => {
  const [isFormOne, setFormOne] = useState(true);

  const openExamData = useSelector((state) => state.model.isOpen15);
  const groups = useSelector((state) => state.groups.groups);
  const isLoading = useSelector((state) => state.chapters.isLoading);

  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: mapExamToFormValues(null, true),
    shouldUnregister: false,
  });

  useEffect(() => {
    if (openExamData) {
      dispatch(getAllChaptersRequest());
      if (groups?.length == 0) dispatch(getAllGroupsRequest());
      dispatch(getAllVideosRequest());
      reset(mapExamToFormValues(data, isAdd, fixedType));
      setFormOne(true);
    }
  }, [openExamData, isAdd, data?.id, reset, fixedType]);

  const watchSubType = watch("sub_type") || data?.sub_type;
  const watchTotalQustions = watch("question_number") || data?.question_number;
  const watchChapters = watch("chapters") || [];
  const watchTimer = watch("timer") ?? data?.timer;
  const watchQuestionWay = watch("random") ?? data?.random;
  const watchType = watch("type") || data?.type;

  const header = (
    <Box>
      <Heading textAlign="center" color="main_1" fontSize="24px">
        {isAdd ? "اضافة اختبار" : "تعديل اختبار"}
      </Heading>
    </Box>
  );

  const onSubmit = async (rawValues) => {
    const values =
      fixedType != null
        ? { ...rawValues, type: String(fixedType) }
        : rawValues;
    if (
      values?.start_date &&
      values?.end_date &&
      values.end_date <= values.start_date
    ) {
      toast.error("تاريخ النهاية يجب أن يكون بعد تاريخ البداية");
      return;
    }

    const formData = new FormData();
    const skipKeys = new Set([
      "groups",
      "chapters",
      "essay_type",
      "subject",
      "video",
      "payment_subject",
      "publish",
      "redo",
      "publish_exam",
      "restrict",
      "edit",
      "delete",
      "Actions",
      "title",
      "id",
      "start_date",
      "end_date",
    ]);

    Object.entries(values || {}).forEach(([key, value]) => {
      if (skipKeys.has(key)) return;
      if (value == null || typeof value === "object") return;
      formData.append(key, value);
    });

    (values?.groups || []).forEach((group, idx) => {
      const groupId = group?.id ?? group;
      if (groupId == null || groupId === "") return;
      formData.append(`groups[${idx}]`, groupId);
    });

    const chapters = appendChapters(
      formData,
      values?.chapters,
      values?.question_number
    );

    formData.append("start_date", toServerDateTime(values?.start_date));
    formData.append("end_date", toServerDateTime(values?.end_date));

    if (values?.timer == 0 || values?.timer == "0") {
      formData.append("time", 0);
    }

    const essayType = values?.essay_type;
    if (
      essayType === "0" ||
      essayType === "1" ||
      essayType === 0 ||
      essayType === 1
    ) {
      formData.append("essay_type", essayType);
    } else {
      formData.append("essay_type", "");
    }

    const action = (response) => {
      dispatch(closeCreateExamDataModel());
      setSelectedData({});
      reset(mapExamToFormValues(null, true, fixedType));
      setFormOne(true);

      setExamData({
        chapter_id: chapters?.[0]?.chapter_id,
        exam: response?.id,
        question_number: response?.question_number,
      });
      isAdd && watchQuestionWay == 0 && dispatch(openQuestionsListModel());
      if (typeof onSaved === "function") onSaved();
    };
    if (isAdd) {
      dispatch(createExamRequest(formData, action));
    } else {
      formData.append("_method", "PUT");

      dispatch(
        editExamRequest({
          id: data?.id,
          formData: formData,
          action: action,
        })
      );
    }
  };

  const closeFunc = () => {
    dispatch(closeCreateExamDataModel());
    setSelectedData({});
    reset(mapExamToFormValues(null, true, fixedType));
    setFormOne(true);
  };

  return (
    <MainModel
      isLoading={isLoading}
      header={header}
      closeFunc={closeFunc}
      openVar={openExamData}
      buttonTitle={" حفظ التغيرات"}
      buttonFunc={handleSubmit(onSubmit)}
      extraButtons={
        <Button
          onClick={() => setFormOne(!isFormOne)}
          color="white"
          bg="main_1"
          _focus={{ outline: "none" }}
          minW="200px"
          my={2}
        >
          {isFormOne ? " الخطوة التالية" : "الخطوة السابقة"}
        </Button>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <HStack dir="rtl" w="100%" justifyContent="center" spacing="50px">
          <HStack onClick={() => setFormOne(true)} cursor="pointer">
            <Circle
              size="40px"
              bg={isFormOne ? "main_1" : "white"}
              color={isFormOne ? "white" : "main_1"}
              borderColor="main_1"
              border="1px solid"
            >
              1
            </Circle>
            <Text>معلومات عن الاختبار</Text>
          </HStack>
          <HStack onClick={() => setFormOne(false)} cursor="pointer">
            <Circle
              size="40px"
              bg={!isFormOne ? "main_1" : "white"}
              color={!isFormOne ? "white" : "main_1"}
              borderColor="main_1"
              border="1px solid"
            >
              2
            </Circle>
            <Text>أسئلة الاختبار</Text>
          </HStack>
        </HStack>

        <StepOne
          data={data}
          register={register}
          watch={watch}
          watchTimer={watchTimer}
          errors={errors}
          isFormOne={isFormOne}
          watchType={watchType}
          setValue={setValue}
          isAdd={isAdd}
          fixedType={fixedType}
        />
        <StepTwo
          watchSubType={watchSubType}
          watchTotalQustions={watchTotalQustions}
          watchQuestionWay={watchQuestionWay}
          data={data}
          setValue={setValue}
          register={register}
          watch={watch}
          errors={errors}
          isFormOne={isFormOne}
          watchChapters={watchChapters}
          isAdd={isAdd}
        />
      </form>
    </MainModel>
  );
};

export default AddExamData;
